import { c as _c } from "react/compiler-runtime";
import { FeatherAlertTriangle, FeatherCheck, FeatherCheckCheck, FeatherInfo } from '@subframe/core';
import { DocTituloSecao } from '@/shared/components/documentos/paginado';
const PASSOS_VERIFICACAO = ['Insira o código de autenticação ao lado', 'Confira se as informações correspondem ao documento recebido'];
const GARANTIAS = ['Identificação do dispositivo (IP, navegador, sistema operacional)', 'Hash de integridade (SHA-256)', 'Carimbo de data e hora (timestamp UTC-3)'];
interface SecaoAutenticidadeProps {
  codigoAutenticacao: string | null;
  urlAutenticacao: string | null;
  urlAutenticacaoExibicao: string | null;
}

/**
 * Sem código não há o que conferir: os três passos ficavam na tela com o campo em branco e um link
 * sem destino, instruindo o leitor a validar um documento que a página pública não resolveria. O
 * certificado pode dizer que a conferência ainda não está disponível; não pode prometê-la.
 *
 * O texto afirma a indisponibilidade, não QUAL campo falta: o back deriva os três do mesmo código
 * (`urlValidacao = codigo ? montarUrlValidacao(codigo) : null`), mas o schema os valida como
 * anuláveis independentes — e num estado misto "não possui código de autenticação" seria falso.
 */
function AutenticidadeIndisponivel() {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <DocTituloSecao icon={<FeatherCheckCheck className="h-4 w-4" />}>Como posso confirmar a autenticidade deste certificado?</DocTituloSecao>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="px-[37px] pb-[26px]">{t0}<div className="border-warning-200 bg-warning-50 flex items-start gap-[10px] rounded-[14px] border border-solid px-7 py-[22px]"><FeatherAlertTriangle className="text-warning-700 mt-[2px] h-4 w-4 flex-none" /><p className="text-warning-800 text-[13.5px] leading-[1.6]">A conferência pública deste certificado na Plataforma Arbitralis ainda não está disponível. Ela é liberada com a emissão do código de autenticação.</p></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function SecaoAutenticidade(t0) {
  const $ = _c(17);
  const {
    codigoAutenticacao,
    urlAutenticacao,
    urlAutenticacaoExibicao
  } = t0;
  if (!codigoAutenticacao || !urlAutenticacao || !urlAutenticacaoExibicao) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <AutenticidadeIndisponivel />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <DocTituloSecao icon={<FeatherCheckCheck className="h-4 w-4" />}>Como posso confirmar a autenticidade deste certificado?</DocTituloSecao>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="text-brand-600 mb-2 text-[16px] font-bold">Verifique este documento em 3 passos</div>;
    t3 = <p className="mb-4 text-[13.5px] leading-[1.5] text-neutral-700">Você pode validar e conferir a legitimidade deste documento através da Plataforma Arbitralis.</p>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="bg-brand-600 flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-bold text-white">1</span>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== urlAutenticacao || $[6] !== urlAutenticacaoExibicao) {
    t5 = <div className="flex items-center gap-3">{t4}<span>Acesse{" "}<a href={urlAutenticacao} target="_blank" rel="noopener noreferrer" className="text-brand-600 font-bold hover:underline">{urlAutenticacaoExibicao}</a></span></div>;
    $[5] = urlAutenticacao;
    $[6] = urlAutenticacaoExibicao;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = PASSOS_VERIFICACAO.map(_temp);
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <div className="flex-1">{t2}{t3}<div className="flex flex-col gap-[11px] text-[13.5px] text-neutral-900">{t5}{t6}</div></div>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="mb-3 text-[11px] font-bold tracking-[1.2px] text-neutral-500 uppercase">Código de Autenticação</div>;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== codigoAutenticacao) {
    t9 = <div className="w-[280px] self-start rounded-[12px] bg-neutral-100 px-6 py-[22px]">{t8}<div className="text-brand-600 text-[18px] font-bold tracking-[0.5px]">{codigoAutenticacao}</div></div>;
    $[12] = codigoAutenticacao;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t7 || $[15] !== t9) {
    t10 = <div className="px-[37px] pb-[26px]">{t1}<div className="border-brand-100 flex gap-6 rounded-[14px] border border-solid px-7 py-[22px]">{t7}{t9}</div></div>;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  return t10;
}
function _temp(passo, index) {
  return <div key={passo} className="flex items-center gap-3"><span className="bg-brand-600 flex h-5 w-5 flex-none items-center justify-center rounded-full text-[11px] font-bold text-white">{index + 2}</span><span>{passo}</span></div>;
}
function textoCadeiaIntegra(rotulo: string): string {
  return `Verificação de integridade${rotulo}: cadeia íntegra (verificada nesta leitura)`;
}

// Sóbrio de propósito: a causa conhecida da divergência é técnica (o trigger do banco reescreve o
// hash — ref. interna #1095), não fraude. O texto não pode nem afirmar integridade que não existe,
// nem admitir adulteração que não houve. O número do ticket fica NESTE comentário e sai da tela:
// é referência nossa, não do usuário (86ak89nxk / D4). O hash SHA-256 da emissão permanece.
// "Não confirmada", e não "em revisão": ninguém é avisado da divergência, e o texto não promete que
// alguém está olhando (Jonathan, 03/11/2026; alerta com dono na core#2613). Igual ao PDF do core
// (`secaoAutenticidade.ts`) e à página pública (`textosDaCadeia.ts`): mudar aqui é mudar lá.

// Com rótulo, a cadeia é uma das trilhas que o documento lê, e não "desta emissão".
function textoCadeiaDivergente(quebradaNaVersao: number | null, rotulo: string): string {
  const origem = quebradaNaVersao === null ? `divergência detectada na cadeia de hashes${rotulo || ' desta emissão'}` : `divergência detectada a partir da versão ${quebradaNaVersao}`;
  return `Verificação de integridade${rotulo}: ${origem}. Os eventos permanecem íntegros individualmente, mas a integridade da cadeia não pôde ser confirmada. Em caso de dúvida, entre em contato com a Câmara.`;
}
interface VerificacaoCadeiaProps {
  cadeiaIntegra: boolean | null;
  cadeiaQuebradaNaVersao: number | null;
  /** Vazio com uma cadeia só; com duas, diz de qual trilha é a verificação. */
  rotulo: string;
}

/** Resultado REAL do `verifyChain`. `null` = back não reportou: não afirma nada (D3, core#1256). */
function VerificacaoCadeia(t0) {
  const $ = _c(11);
  const {
    cadeiaIntegra,
    cadeiaQuebradaNaVersao,
    rotulo
  } = t0;
  if (cadeiaIntegra === null) {
    return null;
  }
  if (cadeiaIntegra) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <span className="bg-success-600 flex h-5 w-5 flex-none items-center justify-center rounded-full"><FeatherCheck className="h-3 w-3 text-white" /></span>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== rotulo) {
      t2 = textoCadeiaIntegra(rotulo);
      $[1] = rotulo;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== t2) {
      t3 = <p className="mb-4 flex items-center gap-[10px] text-[13px] text-neutral-600">{t1}{t2}</p>;
      $[3] = t2;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    return t3;
  }
  let t1;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <FeatherAlertTriangle className="text-warning-700 mt-[2px] h-4 w-4 flex-none" />;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== cadeiaQuebradaNaVersao || $[7] !== rotulo) {
    t2 = textoCadeiaDivergente(cadeiaQuebradaNaVersao, rotulo);
    $[6] = cadeiaQuebradaNaVersao;
    $[7] = rotulo;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== t2) {
    t3 = <div className="border-warning-200 bg-warning-50 mb-4 flex items-start gap-[10px] rounded-[12px] border border-solid px-5 py-[13px]">{t1}<p className="text-warning-800 text-[13px] leading-[1.6]">{t2}</p></div>;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
}

/**
 * Uma segunda trilha que o documento lê, com a própria verificação. O Certificado de Acessos da
 * Sentença lê a das aberturas (as props principais) e a das comunicações, onde está a ENTREGA.
 */
interface CadeiaAdicionalDoDocumento {
  rotulo: string;
  hashTopo: string | null;
  versaoTopo: number | null;
  integra: boolean;
  quebradaNaVersao: number | null;
}
function LinhaDoHash(t0) {
  const $ = _c(8);
  const {
    rotulo,
    hash,
    versao
  } = t0;
  let t1;
  if ($[0] !== hash) {
    t1 = <span className="font-mono text-neutral-700">{hash}</span>;
    $[0] = hash;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== versao) {
    t2 = versao !== null && <> · versão {versao}</>;
    $[2] = versao;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== rotulo || $[5] !== t1 || $[6] !== t2) {
    t3 = <p className="mb-4 text-[12.5px] leading-[1.6] text-neutral-500">Hash de integridade{rotulo} (SHA-256):{" "}{t1}{t2}</p>;
    $[4] = rotulo;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
interface SecaoSegurancaProps {
  paragrafos: string[];
  hashCertificado: string | null;
  versaoHash: number;
  cadeiaIntegra: boolean | null;
  cadeiaQuebradaNaVersao: number | null;
  /** Com ela, as props acima descrevem a cadeia das aberturas e cada uma ganha o seu rótulo. */
  cadeiaAdicional?: CadeiaAdicionalDoDocumento | null;
}
const ROTULO_DA_CADEIA_PRINCIPAL_COM_ADICIONAL = ' dos acessos ao documento';
export function SecaoSeguranca(t0) {
  const $ = _c(27);
  const {
    paragrafos,
    hashCertificado,
    versaoHash,
    cadeiaIntegra,
    cadeiaQuebradaNaVersao,
    cadeiaAdicional: t1
  } = t0;
  const cadeiaAdicional = t1 === undefined ? null : t1;
  const afirmaPrevencaoDeAdulteracao = cadeiaIntegra !== false && cadeiaAdicional?.integra !== false;
  const rotuloPrincipal = cadeiaAdicional ? ROTULO_DA_CADEIA_PRINCIPAL_COM_ADICIONAL : "";
  let t2;
  if ($[0] !== paragrafos) {
    t2 = paragrafos.map(_temp2);
    $[0] = paragrafos;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="mb-5 flex flex-col gap-3">{GARANTIAS.map(_temp3)}</div>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== afirmaPrevencaoDeAdulteracao) {
    t4 = afirmaPrevencaoDeAdulteracao && <p className="mb-4 text-[13.5px] leading-[1.65] text-neutral-600"><strong className="text-neutral-700">Como a integridade é garantida:</strong> cada evento recebe um hash SHA-256 no instante do registro. Qualquer alteração posterior — ainda que de um único caractere — altera o hash e torna a adulteração evidente. Os carimbos de data e hora seguem o horário oficial de Brasília (UTC-3) e os registros são imutáveis após a gravação, permitindo perícia técnica independente.</p>;
    $[3] = afirmaPrevencaoDeAdulteracao;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== cadeiaAdicional) {
    t5 = cadeiaAdicional?.hashTopo && <LinhaDoHash hash={cadeiaAdicional.hashTopo} rotulo={cadeiaAdicional.rotulo} versao={cadeiaAdicional.versaoTopo} />;
    $[5] = cadeiaAdicional;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== cadeiaAdicional) {
    t6 = cadeiaAdicional && <VerificacaoCadeia cadeiaIntegra={cadeiaAdicional.integra} cadeiaQuebradaNaVersao={cadeiaAdicional.quebradaNaVersao} rotulo={cadeiaAdicional.rotulo} />;
    $[7] = cadeiaAdicional;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== hashCertificado || $[10] !== rotuloPrincipal || $[11] !== versaoHash) {
    t7 = hashCertificado && <LinhaDoHash hash={hashCertificado} rotulo={rotuloPrincipal || " desta emiss\xE3o"} versao={versaoHash} />;
    $[9] = hashCertificado;
    $[10] = rotuloPrincipal;
    $[11] = versaoHash;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== cadeiaIntegra || $[14] !== cadeiaQuebradaNaVersao || $[15] !== rotuloPrincipal) {
    t8 = <VerificacaoCadeia cadeiaIntegra={cadeiaIntegra} cadeiaQuebradaNaVersao={cadeiaQuebradaNaVersao} rotulo={rotuloPrincipal} />;
    $[13] = cadeiaIntegra;
    $[14] = cadeiaQuebradaNaVersao;
    $[15] = rotuloPrincipal;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== afirmaPrevencaoDeAdulteracao) {
    t9 = afirmaPrevencaoDeAdulteracao && <div className="bg-brand-50 flex w-fit items-center gap-[10px] rounded-full px-5 py-[11px] text-[13px] text-neutral-600"><FeatherInfo className="text-coral-500 h-4 w-4 flex-none" />Esses mecanismos garantem a autenticidade, impedem adulteração e permitem auditoria técnica independente.</div>;
    $[17] = afirmaPrevencaoDeAdulteracao;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t2 || $[20] !== t4 || $[21] !== t5 || $[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t10 = <div className="px-[37px] pb-6">{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}</div>;
    $[19] = t2;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  return t10;
}
function _temp3(garantia) {
  return <div key={garantia} className="flex items-center gap-3 text-[14px] text-neutral-700"><span className="bg-success-600 flex h-5 w-5 flex-none items-center justify-center rounded-full"><FeatherCheck className="h-3 w-3 text-white" /></span>{garantia}</div>;
}
function _temp2(paragrafo) {
  return <p key={paragrafo} className="mb-4 text-[14px] leading-[1.6] text-neutral-700">{paragrafo}</p>;
}